import * as Sentry from '@sentry/browser';

/**
 * Lives here rather than in `src/lib/sentry` so that Redux slices can import it:
 * that module reaches `src/state/reducers` via `src/StateObserver`, so importing
 * it from a slice closes a cycle. Keep this module a leaf.
 *
 * `src/lib/sentry` re-exports it, so existing callers are unaffected.
 */
export function captureGenericError(
  effect: string,
  cause: any,
  context?: Parameters<(typeof Sentry)['captureException']>[1],
) {
  // Report error to console in development
  if (process.env.IS_DEVELOPMENT) {
    // eslint-disable-next-line no-console
    console.error(effect, cause);
  }

  if (cause instanceof Error) {
    try {
      // Try to overwrite the error message...
      cause.message = `${effect} (${cause.message})`;
      Sentry.captureException(cause, context);
    } catch (e) {
      // If message overwrite fails (e.g. TypeError: Cannot assign to read only property 'message') then create a new Error for this
      const newError = new Error();
      const props = Object.getOwnPropertyDescriptors(cause);
      // Rewrite the message - it should be writable here
      props.message = {
        value: `${effect} (${cause.message})`,
        writable: true,
        enumerable: true,
        configurable: true,
      };
      // Copy all own properties from `cause` to `newError`, including non-enumerable ones
      Object.defineProperties(newError, props);
      // Capture the new error that should be the same as the original one, but with a new message
      Sentry.captureException(newError, context);
    }
  } else {
    const exception = cause
      ? Error(`${effect} (non-Error: ${JSON.stringify(cause)})`)
      : Error(effect);

    (exception as any).framesToPop = 1;

    Sentry.captureException(exception, context);
  }
}
